import { useState, useEffect } from 'react';
import { Map, Marker, Overlay } from 'pigeon-maps';

const COLORS = {
  current: '#2563eb',
  clockIn: '#10b981',
  clockOut: '#ef4444',
  breakStart: '#f97316',
  breakEnd: '#a855f7',
  emerald: '#10b981',
  red: '#ef4444',
  currentLocation: '#1d4ed8',
};

// Parse "lat,lng" string to [lat, lng] array
function parseCoordinates(value) {
  if (!value) return null;

  if (Array.isArray(value) && value.length >= 2) {
    const lat = Number(value[0]);
    const lng = Number(value[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return [lat, lng];
    }
  }

  const str = String(value).replace(/[\[\]\(\)]/g, '').trim();
  const parts = str.split(',');
  if (parts.length === 2) {
    const lat = parseFloat(parts[0].trim());
    const lng = parseFloat(parts[1].trim());
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return [lat, lng];
    }
  }

  // Fallback for formats like "lat: 51.5 long: -0.09" or similar.
  const numericTokens = str.match(/-?\d+(?:\.\d+)?/g);
  if (numericTokens && numericTokens.length >= 2) {
    const lat = parseFloat(numericTokens[0]);
    const lng = parseFloat(numericTokens[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return [lat, lng];
    }
  }

  return null;
}

// Calculate distance between two lat/lng points in meters
function getDistance(lat1, lng1, lat2, lng2) {
  const R = 6371e3; // metres
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) *
    Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d;
}

const GEOFENCE_RADIUS_METERS = 250;

function getGeofenceStatus(coords, companyCoords) {
  if (!coords || !companyCoords) return null;
  const distance = getDistance(coords[0], coords[1], companyCoords[0], companyCoords[1]);
  return distance <= GEOFENCE_RADIUS_METERS ? 'Within Geofence' : 'Outside Geofence';
}

function PulseMarker({ color = COLORS.currentLocation, size = 36 }) {
  const innerSize = size * 0.35;
  const ringSize = size * 0.85;
  const wrapperStyle = {
    position: 'relative',
    width: size,
    height: size,
  };
  const ringStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: ringSize,
    height: ringSize,
    marginLeft: -ringSize / 2,
    marginTop: -ringSize / 2,
    borderRadius: '9999px',
    border: `2px solid ${color}`,
    opacity: 0.7,
    animation: 'maps-pulse 1.8s ease-out infinite',
  };
  const coreStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: innerSize,
    height: innerSize,
    marginLeft: -innerSize / 2,
    marginTop: -innerSize / 2,
    borderRadius: '9999px',
    backgroundColor: color,
    boxShadow: '0 0 0 6px rgba(59, 130, 246, 0.2)',
  };

  return (
    <div style={wrapperStyle}>
      <span style={ringStyle}></span>
      <span style={coreStyle}></span>
    </div>
  );
}

export default function Maps() {
  const [currentClockIns, setCurrentClockIns] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [historicalGeoTags, setHistoricalGeoTags] = useState([]);
  const [companyGeoTag, setCompanyGeoTag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeMarker, setActiveMarker] = useState(null);
  const [historicalMarker, setHistoricalMarker] = useState(null);
  const [showHistorical, setShowHistorical] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fullScreenMap, setFullScreenMap] = useState(null); // 'active' or 'historical'
  const [isMobile, setIsMobile] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 900);

  useEffect(() => {
    const check = () => {
      setIsMobile(window.innerWidth < 640); // tailwind sm breakpoint
      setViewportHeight(window.innerHeight);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const styleId = 'maps-pulse-style';
    if (document.getElementById(styleId)) return undefined;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes maps-pulse {
        0% { transform: scale(0.6); opacity: 0.85; }
        70% { transform: scale(1.4); opacity: 0.25; }
        100% { transform: scale(1.8); opacity: 0; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  // Load current clock-ins, staff, and company geotag on mount
  useEffect(() => {
    async function loadData() {
      try {
        const [clockinsRes, staffRes, companyRes] = await Promise.all([
          fetch('https://api.insightos.co.uk/api/clockins', {
            method: 'GET',
            credentials: 'include',
          }),
          fetch('https://api.insightos.co.uk/api/staff', {
            method: 'GET',
            credentials: 'include',
          }),
          fetch('https://api.insightos.co.uk/api/company-geotag', {
            method: 'GET',
            credentials: 'include',
          }),
        ]);

        if (!clockinsRes.ok || !staffRes.ok || !companyRes.ok) {
          throw new Error(
            `Failed loading map data (clockins:${clockinsRes.status}, staff:${staffRes.status}, company-geotag:${companyRes.status})`
          );
        }

        const clockinsData = await clockinsRes.json();
        const staffData = await staffRes.json();
        const companyData = await companyRes.json();

        console.log('Initial clock-ins data:', clockinsData);
        console.log('Company geotag data:', companyData);
        const resolvedCompanyGeoTag =
          companyData?.companyGeoTag ??
          companyData?.GeoTag ??
          companyData?.data?.GeoTag ??
          companyData?.data?.companyGeoTag ??
          null;

        if (clockinsData.success) setCurrentClockIns(clockinsData.data || []);
        if (staffData.success) setStaff(staffData.data || []);
        setCompanyGeoTag(resolvedCompanyGeoTag);
      } catch (err) {
        console.error('Error loading data:', err);
      }
    }

    loadData();
  }, []);

  const handleFetchHistorical = async () => {
    if (!selectedStaff || !startDate || !endDate) {
      alert('Please select staff and date range');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `https://api.insightos.co.uk/api/getmapclockins?staffId=${selectedStaff}&startDate=${startDate}&endDate=${endDate}`,
        { method: 'GET', credentials: 'include' }
      );
      const json = await res.json();
      const resolvedCompanyGeoTag =
        json?.companyGeoTag ??
        json?.GeoTag ??
        json?.data?.GeoTag ??
        json?.data?.companyGeoTag ??
        null;
      if (json.success) {
        console.log('Historical payload:', json.clockins);
        console.log('Company GeoTag:', resolvedCompanyGeoTag);
        setHistoricalGeoTags(json.clockins || []);
        setCompanyGeoTag(resolvedCompanyGeoTag);
      } else {
        console.warn('Historical fetch returned success:false', json);
        setHistoricalGeoTags([]);
        setCompanyGeoTag(null);
      }
    } catch (err) {
      console.error('Error loading historical data:', err);
    } finally {
      setLoading(false);
    }
  };

  const companyCoords = companyGeoTag ? parseCoordinates(companyGeoTag) : null;
  const activeMapHeight = Math.round(
    isMobile
      ? Math.min(Math.max(viewportHeight * 0.4, 320), 420)
      : Math.min(Math.max(viewportHeight * 0.6, 560), 820)
  );
  const historicalMapHeight = Math.round(
    isMobile
      ? Math.min(Math.max(viewportHeight * 0.4, 320), 420)
      : Math.min(Math.max(viewportHeight * 0.58, 540), 800)
  );
  const fullScreenMapHeight = Math.max(420, viewportHeight - (isMobile ? 112 : 96));

  // Calculate center for current clock-ins map
  const currentCenter = (() => {
    if (currentClockIns.length === 0) return [51.505, -0.09];
    for (const clockin of currentClockIns) {
      const coords = parseCoordinates(clockin.GeoTagClockin);
      if (coords) return coords;
    }
    return [51.505, -0.09];
  })();

  // Calculate center for historical map
  const historicalCenter = (() => {
    if (historicalGeoTags.length === 0) return [51.505, -0.09];
    for (const record of historicalGeoTags) {
      const coords = parseCoordinates(record.GeoTagClockin);
      if (coords) return coords;
    }
    return [51.505, -0.09];
  })();

  // Define markers for reuse
  const activeMarkers = currentClockIns.map((clockin, index) => {
    const coords = parseCoordinates(clockin.GeoTagClockin);
    if (!coords) return null;
    const geofenceStatus = getGeofenceStatus(coords, companyCoords);
    const markerColor = geofenceStatus === 'Within Geofence' ? COLORS.emerald : COLORS.red;
    return (
      <Marker
        key={`current-${index}`}
        anchor={coords}
        color={markerColor}
        width={32}
        height={32}
        onClick={() => {
          setHistoricalMarker(null);
          setActiveMarker({
            name: clockin.StaffName,
            time: clockin.ClockIn,
            type: 'Active Clock-In',
            geofence: geofenceStatus || 'Geofence unavailable',
          });
        }}
      />
    );
  }).filter(Boolean);

  const activeCurrentLocationMarkers = currentClockIns.map((clockin, index) => {
    const coords = parseCoordinates(clockin.CurrentLocation);
    if (!coords) return null;
    const geofenceStatus = getGeofenceStatus(coords, companyCoords);
    return (
      <Overlay key={`current-live-${index}`} anchor={coords} offset={[18, 18]}>
        <button
          type="button"
          style={overlayButtonStyle}
          aria-label={`Current location for ${clockin.StaffName || 'staff member'}`}
          onClick={() => {
            setHistoricalMarker(null);
            setActiveMarker({
              name: clockin.StaffName,
              time: clockin.ClockIn,
              type: 'Current Location',
              geofence: geofenceStatus || 'Geofence unavailable',
            });
          }}
        >
          <PulseMarker color={COLORS.currentLocation} />
        </button>
      </Overlay>
    );
  }).filter(Boolean);

  const companyMarkerActive = companyGeoTag ? (() => {
    const companyCoords = parseCoordinates(companyGeoTag);
    if (companyCoords) {
      return (
        <Marker
          key="company-active"
          anchor={companyCoords}
          color="#000000"
          width={32}
          height={32}
          onClick={() => {
            setHistoricalMarker(null);
            setActiveMarker({ type: 'Company Location', name: 'Company', time: 'N/A' });
          }}
        />
      );
    }
    return null;
  })() : null;

  const historicalMarkers = historicalGeoTags.flatMap((record, index) => {
    const markers = [];
    
    const clockInCoords = parseCoordinates(record.GeoTagClockin);
    if (clockInCoords) {
      markers.push(
        <Marker
          key={`hist-ci-${index}`}
          anchor={clockInCoords}
          color={COLORS.clockIn}
          width={32}
          height={32}
          onClick={() => {
            setActiveMarker(null);
            setHistoricalMarker({ type: 'Clock In', name: record.StaffName || 'Unknown Staff', date: record.Date, time: 'Clock In' });
          }}
        />
      );
    }

    const clockOutCoords = parseCoordinates(record.GeoTagClockout);
    if (clockOutCoords) {
      markers.push(
        <Marker
          key={`hist-co-${index}`}
          anchor={clockOutCoords}
          color={COLORS.clockOut}
          width={32}
          height={32}
          onClick={() => {
            setActiveMarker(null);
            setHistoricalMarker({ type: 'Clock Out', name: record.StaffName || 'Unknown Staff', date: record.Date, time: 'Clock Out' });
          }}
        />
      );
    }

    const breakStartCoords = parseCoordinates(record.GeoTagBreakStart);
    if (breakStartCoords) {
      markers.push(
        <Marker
          key={`hist-bs-${index}`}
          anchor={breakStartCoords}
          color={COLORS.breakStart}
          width={32}
          height={32}
          onClick={() => {
            setActiveMarker(null);
            setHistoricalMarker({ type: 'Break Start', name: record.StaffName || 'Unknown Staff', date: record.Date, time: 'Break Start' });
          }}
        />
      );
    }

    const breakEndCoords = parseCoordinates(record.GeoTagBreakEnd);
    if (breakEndCoords) {
      markers.push(
        <Marker
          key={`hist-be-${index}`}
          anchor={breakEndCoords}
          color={COLORS.breakEnd}
          width={32}
          height={32}
          onClick={() => {
            setActiveMarker(null);
            setHistoricalMarker({ type: 'Break End', name: record.StaffName || 'Unknown Staff', date: record.Date, time: 'Break End' });
          }}
        />
      );
    }

    const currentLocationCoords = parseCoordinates(record.CurrentLocation);
    if (currentLocationCoords) {
      const geofenceStatus = getGeofenceStatus(currentLocationCoords, companyCoords);
      markers.push(
        <Overlay key={`hist-live-${index}`} anchor={currentLocationCoords} offset={[18, 18]}>
          <button
            type="button"
            style={overlayButtonStyle}
            aria-label={`Historical current location for ${record.StaffName || 'staff member'}`}
            onClick={() => {
              setActiveMarker(null);
              setHistoricalMarker({
                type: 'Current Location',
                name: record.StaffName || 'Unknown Staff',
                date: record.Date,
                time: 'Current Location',
                geofence: geofenceStatus || 'Geofence unavailable',
              });
            }}
          >
            <PulseMarker color={COLORS.currentLocation} />
          </button>
        </Overlay>
      );
    }

    return markers;
  });

  const companyMarkerHistorical = companyGeoTag ? (() => {
    const companyCoords = parseCoordinates(companyGeoTag);
    if (companyCoords) {
      return (
        <Marker
          key="company"
          anchor={companyCoords}
          color="#000000"
          width={32}
          height={32}
          onClick={() => {
            setActiveMarker(null);
            setHistoricalMarker({ type: 'Company Location', name: 'Company', time: 'N/A' });
          }}
        />
      );
    }
    return null;
  })() : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-100 px-3 py-6 sm:px-4 sm:py-8 lg:px-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="relative overflow-hidden rounded-3xl border border-white/35 bg-gradient-to-r from-indigo-950 via-sky-700 to-emerald-500 px-5 py-6 shadow-[0_18px_45px_rgba(15,23,42,0.28)] sm:px-8 sm:py-8">
          <div className="pointer-events-none absolute -left-10 top-0 h-36 w-36 rounded-full bg-white/15 blur-3xl" />
          <div className="pointer-events-none absolute -right-8 bottom-0 h-32 w-32 rounded-full bg-cyan-200/20 blur-3xl" />
          <div className="relative z-10 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-sky-100/85">Location Intelligence</p>
              <h1 className="mt-2 text-2xl font-semibold text-white sm:text-3xl">Employee Location Tracker</h1>
              <p className="mt-2 max-w-2xl text-sm text-sky-100/90 sm:text-base">
                Monitor active locations, review historical movement, and check geofence status with a dashboard style aligned to management.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="rounded-2xl border border-white/20 bg-white/10 px-3 py-2 text-xs font-medium text-white/95 shadow-[0_8px_22px_rgba(15,23,42,0.18)] backdrop-blur-xl">
                {currentClockIns.filter((c) => parseCoordinates(c.GeoTagClockin)).length} active markers
              </span>
              <span className="rounded-2xl border border-white/20 bg-white/10 px-3 py-2 text-xs font-medium text-white/95 shadow-[0_8px_22px_rgba(15,23,42,0.18)] backdrop-blur-xl">
                {historicalGeoTags.length} historical records
              </span>
            </div>
          </div>
        </div>

      {/* Active Clock-Ins Map */}
      <div className="w-full overflow-hidden rounded-3xl border border-white/35 bg-gradient-to-br from-indigo-100/80 via-white/75 to-sky-100/70 shadow-[0_16px_40px_rgba(15,23,42,0.18)] backdrop-blur-xl">
        <div className="relative overflow-hidden border-b border-white/30 bg-gradient-to-r from-indigo-900/90 via-sky-700/85 to-emerald-500/80 p-4 sm:p-5">
          <div className="pointer-events-none absolute -left-6 top-0 h-24 w-24 rounded-full bg-white/15 blur-2xl" />
          <div className="pointer-events-none absolute -right-4 bottom-0 h-20 w-20 rounded-full bg-cyan-200/20 blur-2xl" />
          <div className="relative z-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white sm:text-xl">Active Clock-Ins</h2>
            <p className="text-sm text-sky-100/90">
              Showing {currentClockIns.filter((c) => parseCoordinates(c.GeoTagClockin)).length} active employee(s)
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <button
              onClick={() => {
                setIsFullScreen(true);
                setFullScreenMap('active');
              }}
              className="inline-flex items-center justify-center rounded-2xl border border-white/20 bg-white/12 px-3 py-2 text-xs font-semibold text-white shadow-[0_12px_30px_rgba(15,23,42,0.24)] backdrop-blur-xl transition-all hover:bg-white/20 sm:text-sm"
              title="Full Screen"
            >
              <svg className="mr-2 h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
              Expand map
            </button>
            <button
              onClick={() => {
                setShowHistorical((prev) => {
                  const next = !prev;
                  if (!next) {
                    setHistoricalMarker(null);
                  }
                  return next;
                });
              }}
              className="inline-flex items-center justify-center rounded-2xl bg-white px-3 py-2 text-xs font-semibold text-indigo-700 shadow-[0_12px_30px_rgba(15,23,42,0.18)] transition-all hover:bg-slate-50 sm:text-sm"
            >
              {showHistorical ? 'Hide Historical' : 'Show Historical'}
            </button>
          </div>
        </div>
        </div>
        <div className="p-3 sm:p-4 lg:p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 mb-4">
            <div className="rounded-2xl border border-white/40 bg-white/70 px-4 py-3 shadow-[0_12px_30px_rgba(15,23,42,0.08)] backdrop-blur-xl">
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Within Geofence</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">
                {currentClockIns.filter((clockin) => {
                  const coords = parseCoordinates(clockin.GeoTagClockin);
                  return getGeofenceStatus(coords, companyCoords) === 'Within Geofence';
                }).length}
              </p>
            </div>
            <div className="rounded-2xl border border-white/40 bg-white/70 px-4 py-3 shadow-[0_12px_30px_rgba(15,23,42,0.08)] backdrop-blur-xl">
              <p className="text-xs font-semibold uppercase tracking-wide text-rose-700">Outside Geofence</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">
                {currentClockIns.filter((clockin) => {
                  const coords = parseCoordinates(clockin.GeoTagClockin);
                  return getGeofenceStatus(coords, companyCoords) === 'Outside Geofence';
                }).length}
              </p>
            </div>
            <div className="rounded-2xl border border-white/40 bg-white/70 px-4 py-3 shadow-[0_12px_30px_rgba(15,23,42,0.08)] backdrop-blur-xl">
              <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">Live Positions</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">
                {currentClockIns.filter((clockin) => parseCoordinates(clockin.CurrentLocation)).length}
              </p>
            </div>
            <div className="rounded-2xl border border-white/40 bg-white/70 px-4 py-3 shadow-[0_12px_30px_rgba(15,23,42,0.08)] backdrop-blur-xl">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">Company Marker</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">{companyCoords ? 'Available' : 'Not set'}</p>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-[26px] border border-white/35 bg-white/70 shadow-[0_18px_40px_rgba(15,23,42,0.14)] backdrop-blur-xl">
            <Map center={currentCenter} zoom={13} height={activeMapHeight}>
              {activeMarkers}
              {activeCurrentLocationMarkers}
              {companyMarkerActive}
            </Map>
            {activeMarker && (
              <div className="absolute left-3 right-3 top-3 z-10 max-w-sm rounded-2xl border border-white/40 bg-white/88 p-4 shadow-[0_18px_45px_rgba(15,23,42,0.2)] backdrop-blur-xl sm:left-4 sm:right-auto">
                <button
                  onClick={() => setActiveMarker(null)}
                  className="absolute right-3 top-3 rounded-full bg-slate-100 p-1 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div className="space-y-2 pr-6">
                  <h3 className="font-semibold text-slate-900">{activeMarker.type}</h3>
                  <div className="space-y-1 text-sm text-slate-600">
                    {activeMarker.name !== 'Company' && <p>Staff: <span className="font-medium text-slate-900">{activeMarker.name}</span></p>}
                    {activeMarker.date && <p>Date: <span className="font-medium text-slate-900">{new Date(activeMarker.date).toLocaleDateString()}</span></p>}
                    {activeMarker.time && activeMarker.time !== 'N/A' && <p>Time: <span className="font-medium text-slate-900">{activeMarker.time}</span></p>}
                    {activeMarker.geofence && <p>Geofence: <span className="font-medium text-slate-900">{activeMarker.geofence}</span></p>}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2 sm:gap-3">
            <div className="inline-flex items-center gap-2 rounded-2xl border border-white/40 bg-white/70 px-3 py-2 text-xs font-medium text-slate-700 shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl sm:text-sm">
              <span className="inline-block h-3 w-3 rounded-full" style={{ background: COLORS.emerald }}></span>
              Within Geofence
            </div>
            <div className="inline-flex items-center gap-2 rounded-2xl border border-white/40 bg-white/70 px-3 py-2 text-xs font-medium text-slate-700 shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl sm:text-sm">
              <span className="inline-block h-3 w-3 rounded-full" style={{ background: COLORS.red }}></span>
              Outside Geofence
            </div>
            <div className="inline-flex items-center gap-2 rounded-2xl border border-white/40 bg-white/70 px-3 py-2 text-xs font-medium text-slate-700 shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl sm:text-sm">
              <span className="inline-block h-3 w-3 rounded-full" style={{ background: COLORS.currentLocation }}></span>
              Current Location
            </div>
            <div className="inline-flex items-center gap-2 rounded-2xl border border-white/40 bg-white/70 px-3 py-2 text-xs font-medium text-slate-700 shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl sm:text-sm">
              <span className="inline-block h-3 w-3 rounded-full bg-black"></span>
              Company Location
            </div>
          </div>
        </div>
      </div>

      {/* Historical Location Data - Collapsed by default */}
      <div className="w-full rounded-2xl border border-white/35 bg-gradient-to-br from-indigo-100/75 via-sky-100/70 to-emerald-100/65 backdrop-blur-xl shadow-[0_14px_35px_rgba(15,23,42,0.25),inset_0_1px_0_rgba(255,255,255,0.35)] overflow-hidden">
        <div className="relative overflow-hidden p-4 sm:p-6 border-b border-white/30">
          <div className="pointer-events-none absolute -top-8 -left-8 h-24 w-24 rounded-full bg-white/25 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-10 -right-8 h-24 w-24 rounded-full bg-sky-300/20 blur-2xl" />
          <div className="relative z-10 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-indigo-900/90">Historical Location Data</h2>
              <p className="text-sm text-indigo-900/70">
                {showHistorical ? 'Expanded view' : 'Minimised view'}
              </p>
            </div>
            <button
              onClick={() => {
                setShowHistorical((prev) => {
                  const next = !prev;
                  if (!next) {
                    setHistoricalMarker(null);
                  }
                  return next;
                });
              }}
              className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl bg-indigo-900/85 text-white text-xs sm:text-sm font-semibold hover:bg-indigo-800 transition-colors"
            >
              {showHistorical ? 'Minimise' : 'Open Historical'}
            </button>
          </div>
        </div>

        {!showHistorical ? (
          <div className="px-4 sm:px-6 py-4 text-sm text-indigo-900/75">
            Historical map is minimised on desktop and mobile. Select Open Historical to load and view staff history.
          </div>
        ) : (
          <>
            <div className="p-4 sm:p-6 border-b border-white/30 space-y-4">
              <div className="flex items-center justify-end">
                <button
                  onClick={() => {
                    setIsFullScreen(true);
                    setFullScreenMap('historical');
                  }}
                  className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-3 py-2 text-xs font-semibold text-white shadow-[0_12px_30px_rgba(49,46,129,0.28)] transition-all hover:from-indigo-800/95 hover:to-indigo-600/95 sm:text-sm"
                  title="Full Screen"
                >
                  <svg className="mr-2 h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                  Expand map
                </button>
              </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-indigo-900/80 mb-1">Select Staff</label>
                  <select
                    value={selectedStaff}
                    onChange={(e) => setSelectedStaff(e.target.value)}
                        className="w-full rounded-2xl border border-white/45 bg-white/80 px-3 py-2.5 text-sm text-slate-900 shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Choose staff member</option>
                    {staff.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.Name || `${s.FirstName || ''} ${s.LastName || ''}`.trim()}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-indigo-900/80 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full rounded-2xl border border-white/45 bg-white/80 px-3 py-2.5 text-sm shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-indigo-900/80 mb-1">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full rounded-2xl border border-white/45 bg-white/80 px-3 py-2.5 text-sm shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="flex items-end">
                  <button
                    onClick={handleFetchHistorical}
                    disabled={loading}
                    className="w-full rounded-2xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_14px_32px_rgba(49,46,129,0.28)] transition-all hover:from-indigo-800/95 hover:to-indigo-600/95 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-400"
                  >
                    {loading ? 'Loading...' : 'Show on Map'}
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full inline-block" style={{ background: COLORS.clockIn }}></span>
                  <span className="text-slate-700">Clock In</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full inline-block" style={{ background: COLORS.clockOut }}></span>
                  <span className="text-slate-700">Clock Out</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full inline-block" style={{ background: COLORS.breakStart }}></span>
                  <span className="text-slate-700">Break Start</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full inline-block" style={{ background: COLORS.breakEnd }}></span>
                  <span className="text-slate-700">Break End</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full inline-block" style={{ background: '#000000' }}></span>
                  <span className="text-slate-700">Company Location</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full inline-block" style={{ background: COLORS.currentLocation }}></span>
                  <span className="text-slate-700">Current Location</span>
                </div>
              </div>
            </div>

            <div className="px-4 pb-4 sm:px-6 sm:pb-6">
              <div className="relative overflow-hidden rounded-[26px] border border-white/35 bg-white/72 shadow-[0_18px_40px_rgba(15,23,42,0.14)] backdrop-blur-xl">
                <Map center={historicalCenter} zoom={13} height={historicalMapHeight}>
                  {historicalMarkers}
                  {companyMarkerHistorical}
                </Map>
                {historicalMarker && (
                  <div className="absolute left-3 right-3 top-3 z-10 max-w-sm rounded-2xl border border-white/40 bg-white/88 p-4 shadow-[0_18px_45px_rgba(15,23,42,0.2)] backdrop-blur-xl sm:left-4 sm:right-auto">
                    <button
                      onClick={() => setHistoricalMarker(null)}
                      className="absolute right-3 top-3 rounded-full bg-slate-100 p-1 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <div className="space-y-2 pr-6">
                      <h3 className="font-semibold text-slate-900">{historicalMarker.type}</h3>
                      <div className="space-y-1 text-sm text-slate-600">
                        {historicalMarker.name !== 'Company' && (
                          <p>Staff: <span className="font-medium text-slate-900">{historicalMarker.name}</span></p>
                        )}
                        {historicalMarker.date && (
                          <p>Date: <span className="font-medium text-slate-900">{new Date(historicalMarker.date).toLocaleDateString()}</span></p>
                        )}
                        {historicalMarker.time && historicalMarker.time !== 'N/A' && (
                          <p>Type: <span className="font-medium text-slate-900">{historicalMarker.time}</span></p>
                        )}
                        {historicalMarker.geofence && (
                          <p>Geofence: <span className="font-medium text-slate-900">{historicalMarker.geofence}</span></p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {isMobile && (
                <div className="mt-3 rounded-2xl border border-white/35 bg-white/70 px-4 py-3 text-sm text-slate-600 shadow-[0_10px_26px_rgba(15,23,42,0.08)] backdrop-blur-xl">
                  Tap Expand map for a larger view when reviewing dense historical activity on mobile.
                </div>
              )}
            </div>

            {historicalGeoTags.length > 0 && (
              <div className="border-t border-white/30 bg-white/45 p-4 text-sm text-slate-600 backdrop-blur-xl">
                Showing location data for selected staff and date range
              </div>
            )}
          </>
        )}
      </div>

      {/* Full Screen Modal */}
      {isFullScreen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/75 p-3 sm:p-4 backdrop-blur-sm">
          <div className="relative h-full w-full max-w-7xl overflow-hidden rounded-[28px] border border-white/20 bg-white/90 shadow-[0_20px_60px_rgba(15,23,42,0.35)] backdrop-blur-xl">
            <div className="absolute top-4 right-4 z-10">
              <button
                onClick={() => {
                  setIsFullScreen(false);
                  setFullScreenMap(null);
                }}
                className="rounded-full bg-white/90 p-2 text-slate-800 shadow-[0_12px_30px_rgba(15,23,42,0.25)] transition-colors hover:bg-white"
                title="Close Full Screen"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="h-full w-full p-2 sm:p-3">
              <div className="h-full overflow-hidden rounded-[24px] border border-white/35 bg-slate-100/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.4)]">
              {fullScreenMap === 'active' && (
                <Map center={currentCenter} zoom={13} height={fullScreenMapHeight}>
                  {activeMarkers}
                  {activeCurrentLocationMarkers}
                  {companyMarkerActive}
                </Map>
              )}
              {fullScreenMap === 'historical' && (
                <Map center={historicalCenter} zoom={13} height={fullScreenMapHeight}>
                  {historicalMarkers}
                  {companyMarkerHistorical}
                </Map>
              )}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

const overlayButtonStyle = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
};